import React, { useEffect, useState } from "react";
import {
  LogOut,
  Eye,
  Users,
  MousePointerClick,
  MessageCircle,
  Activity,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Lock,
  BarChart3,
  Boxes,
  Megaphone,
  MapPin,
  Building2,
  TrendingUp,
  FileSpreadsheet,
  FileText,
  Search,
  ShoppingCart,
  PackageX,
  LayoutGrid,
  Sparkles,
} from "lucide-react";
import { API } from "../analytics";
import AdminInventoryFile from "./AdminInventoryFile";
import AdminMarketing from "./AdminMarketing";
import AdminSiteCategories from "./AdminSiteCategories";
import AdminSiteMedia from "./AdminSiteMedia";
import AdminPopups from "./AdminPopups";

const TOKEN_KEY = "tcs_admin_token";

function LoginForm({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        setError("Invalid username or password");
        setLoading(false);
        return;
      }
      const data = await res.json();
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess();
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-slate-200 p-8"
      >
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-green-600/10 flex items-center justify-center">
            <Lock className="w-7 h-7 text-green-600" />
          </div>
          <h1 className="mt-4 text-2xl font-extrabold text-slate-800">Admin Login</h1>
          <p className="text-slate-500 text-sm mt-1">Tele-Comp Solutions Analytics</p>
        </div>
        <div className="mt-6 space-y-4">
          <div>
            <label className="text-sm font-semibold text-slate-700">Username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-4 py-2.5 rounded-lg border border-slate-300 outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-bold transition-colors"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 min-w-0">
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs sm:text-sm text-slate-500 font-medium min-w-0 break-words">{label}</span>
      <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-green-600/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
      </span>
    </div>
    <div className="mt-2 sm:mt-3 text-2xl sm:text-3xl font-extrabold text-slate-800 truncate">{value}</div>
  </div>
);

const niceCeil = (v) => {
  if (v <= 5) return 5;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return m * p;
};

const TrendChart = ({ data }) => {
  const [hover, setHover] = useState(null);
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3"><Activity className="w-5 h-5 text-green-600" /><h3 className="font-bold text-slate-800">Daily Traffic</h3></div>
        <p className="text-sm text-slate-400 py-10 text-center">No traffic data for this range yet.</p>
      </div>
    );
  }
  const W = 720, H = 220, padL = 36, padR = 14, padT = 16, padB = 28;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const len = data.length;
  const views = data.map((d) => d.views || 0);
  const visitors = data.map((d) => d.visitors || 0);
  const niceMax = niceCeil(Math.max(1, ...views, ...visitors));
  const x = (i) => (len > 1 ? padL + (i / (len - 1)) * plotW : padL + plotW / 2);
  const y = (v) => padT + (1 - v / niceMax) * plotH;
  const lineOf = (arr) => arr.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const viewsLine = lineOf(views);
  const visitorsLine = lineOf(visitors);
  const area = `${viewsLine} L${x(len - 1).toFixed(1)},${(H - padB).toFixed(1)} L${x(0).toFixed(1)},${(H - padB).toFixed(1)} Z`;

  const total = views.reduce((a, b) => a + b, 0);
  const peak = Math.max(...views);
  const avg = Math.round(total / len);
  const grid = [0, 0.25, 0.5, 0.75, 1];
  // x labels: ~5 evenly spaced
  const labelStep = Math.max(1, Math.ceil(len / 5));
  const fmt = (d) => (d || "").slice(5);

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const idx = Math.round(frac * (len - 1));
    setHover(idx);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2"><Activity className="w-5 h-5 text-green-600" /><h3 className="font-bold text-slate-800">Daily Traffic</h3></div>
        <div className="flex items-center gap-4">
          <div className="text-right"><div className="text-xs text-slate-400">Total views</div><div className="text-lg font-extrabold text-slate-800 leading-none">{total.toLocaleString()}</div></div>
          <div className="text-right"><div className="text-xs text-slate-400">Peak day</div><div className="text-lg font-extrabold text-slate-800 leading-none">{peak.toLocaleString()}</div></div>
          <div className="text-right"><div className="text-xs text-slate-400">Avg / day</div><div className="text-lg font-extrabold text-slate-800 leading-none">{avg.toLocaleString()}</div></div>
        </div>
      </div>
      <div className="flex items-center gap-4 mb-2 text-xs font-semibold">
        <span className="inline-flex items-center gap-1.5 text-slate-600"><span className="w-3 h-1.5 rounded-full bg-green-600"></span> Page views</span>
        <span className="inline-flex items-center gap-1.5 text-slate-600"><span className="w-3 h-1.5 rounded-full bg-sky-500"></span> Unique visitors</span>
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[230px]" onMouseMove={onMove} onMouseLeave={() => setHover(null)} data-testid="analytics-trend-svg">
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#16a34a" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* gridlines + y labels */}
          {grid.map((t, i) => {
            const yy = padT + (1 - t) * plotH;
            return (
              <g key={i}>
                <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="#eef2f6" strokeWidth="1" />
                <text x={padL - 6} y={yy + 3} textAnchor="end" fontSize="10" fill="#94a3b8">{Math.round(niceMax * t).toLocaleString()}</text>
              </g>
            );
          })}
          {/* x labels */}
          {data.map((d, i) => (i % labelStep === 0 || i === len - 1) ? (
            <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#94a3b8">{fmt(d.date)}</text>
          ) : null)}
          <path d={area} fill="url(#trendFill)" />
          <path d={visitorsLine} fill="none" stroke="#0ea5e9" strokeWidth="2" strokeDasharray="4 3" opacity="0.9" />
          <path d={viewsLine} fill="none" stroke="#16a34a" strokeWidth="2.5" />
          {hover != null && (
            <g>
              <line x1={x(hover)} y1={padT} x2={x(hover)} y2={H - padB} stroke="#cbd5e1" strokeWidth="1" />
              <circle cx={x(hover)} cy={y(visitors[hover])} r="3.5" fill="#0ea5e9" stroke="#fff" strokeWidth="1.5" />
              <circle cx={x(hover)} cy={y(views[hover])} r="4" fill="#16a34a" stroke="#fff" strokeWidth="1.5" />
            </g>
          )}
        </svg>
        {hover != null && (
          <div
            className="absolute -translate-x-1/2 -translate-y-full pointer-events-none bg-slate-900 text-white rounded-lg px-3 py-2 shadow-lg text-xs whitespace-nowrap z-10"
            style={{ left: `${(x(hover) / W) * 100}%`, top: `${(y(Math.max(views[hover], visitors[hover])) / H) * 100}%`, marginTop: -8 }}
            data-testid="analytics-trend-tooltip"
          >
            <div className="font-bold mb-1">{data[hover].date}</div>
            <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500"></span> {views[hover].toLocaleString()} views</div>
            <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-sky-400"></span> {visitors[hover].toLocaleString()} visitors</div>
          </div>
        )}
      </div>
    </div>
  );
};

const BarList = ({ title, icon: Icon, data, iconMap, tone = "green", subtitle }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  const bar = tone === "amber" ? "bg-amber-500" : "bg-green-500";
  const iconColor = tone === "amber" ? "text-amber-600" : "text-green-600";
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className={`w-5 h-5 ${iconColor}`} />}
        <h3 className="font-bold text-slate-800">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-slate-400 mb-3 ml-7 -mt-0.5">{subtitle}</p>}
      {!subtitle && <div className="mb-3" />}
      {data.length === 0 ? (
        <p className="text-sm text-slate-400">No data yet.</p>
      ) : (
        <div className="space-y-3">
          {data.map((d) => {
            const RowIcon = iconMap ? iconMap[d.name] : null;
            return (
              <div key={d.name}>
                <div className="flex justify-between text-sm mb-1 gap-2">
                  <span className="text-slate-700 font-medium flex items-center gap-1.5 truncate min-w-0">
                    {RowIcon && <RowIcon className="w-4 h-4 text-slate-400 shrink-0" />}
                    <span className="truncate">{d.name || "—"}</span>
                  </span>
                  <span className="text-slate-500 shrink-0">{d.count}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${bar} rounded-full`}
                    style={{ width: `${(d.count / max) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

function Dashboard({ onLogout }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [range, setRange] = useState("30"); // 7 | 30 | 90 | custom
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  useEffect(() => {
    let active = true;
    const token = localStorage.getItem(TOKEN_KEY);
    const qs =
      range === "custom" && customStart && customEnd
        ? `start=${customStart}&end=${customEnd}`
        : `days=${range === "custom" ? 30 : range}`;
    const loadData = async () => {
      try {
        const res = await fetch(`${API}/admin/analytics?${qs}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active) return;
        if (res.status === 401) {
          onLogout();
          return;
        }
        if (!res.ok) {
          setError("Failed to load analytics.");
          return;
        }
        const json = await res.json();
        if (active) setData(json);
      } catch (e) {
        if (active) setError("Failed to load analytics.");
      }
    };
    loadData();
    const id = setInterval(loadData, 30000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [onLogout, range, customStart, customEnd]);

  if (error) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-600">
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-500">
        Loading analytics…
      </div>
    );
  }

  const t = data.totals;
  const deviceIcons = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
  const prettyPages = data.pages.map((p) => ({
    ...p,
    name: p.name === "/" ? "Home" : p.name.replace("/", "").replace(/^\w/, (c) => c.toUpperCase()),
  }));
  const prettyLangs = data.languages.map((l) => ({
    ...l,
    name: l.name === "es" ? "Spanish" : "English",
  }));

  const exportQs =
    range === "custom" && customStart && customEnd
      ? `start=${customStart}&end=${customEnd}`
      : `days=${range === "custom" ? 30 : range}`;
  const downloadExport = async (fmt) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API}/admin/analytics/export.${fmt}?${exportQs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `telecomp-analytics.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      /* noop */
    }
  };

  return (
    <div className="space-y-6" data-testid="analytics-view">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">Dashboard</h1>
          <p className="text-slate-500">
            {data.range ? `${data.range.start} → ${data.range.end}` : "Live overview"} · auto-refreshes
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="analytics-range">
          <div className="inline-flex bg-slate-100 rounded-full p-1">
            {[["7", "7d"], ["30", "30d"], ["90", "90d"]].map(([v, label]) => (
              <button
                key={v}
                onClick={() => setRange(v)}
                data-testid={`range-${v}`}
                className={`px-3 py-1.5 rounded-full text-sm font-bold ${range === v ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
              >
                {label}
              </button>
            ))}
            <button
              onClick={() => setRange("custom")}
              data-testid="range-custom"
              className={`px-3 py-1.5 rounded-full text-sm font-bold ${range === "custom" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
            >
              Custom
            </button>
          </div>
          {range === "custom" && (
            <div className="flex items-center gap-1.5">
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="form-input py-1.5 text-sm" data-testid="range-start" />
              <span className="text-slate-400">–</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="form-input py-1.5 text-sm" data-testid="range-end" />
            </div>
          )}
          <button onClick={() => downloadExport("csv")} data-testid="analytics-export-csv" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 font-bold text-sm hover:border-green-400">
            <FileSpreadsheet className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => downloadExport("pdf")} data-testid="analytics-export-pdf" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 font-bold text-sm hover:border-green-400">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          <StatCard icon={Eye} label="Total Page Views" value={t.pageviews} />
          <StatCard icon={Users} label="Unique Visitors" value={t.unique_visitors} />
          <StatCard icon={MessageCircle} label="WhatsApp Clicks" value={t.whatsapp_clicks} />
          <StatCard icon={MousePointerClick} label="Quote Clicks" value={t.quote_clicks} />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          <StatCard icon={Activity} label="Sessions" value={t.sessions} />
          <StatCard icon={TrendingUp} label="Views / Visitor" value={t.views_per_visitor ?? "—"} />
          <StatCard icon={MousePointerClick} label="Engagement Rate" value={`${t.engagement_rate ?? 0}%`} />
          <StatCard icon={Globe} label="Countries" value={t.countries_count ?? 0} />
        </div>

        <TrendChart data={data.trend} />

        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <MapPin className="w-5 h-5 text-green-600" />
            <h3 className="font-bold text-slate-800">Visitor Locations</h3>
            <span className="text-xs text-slate-400">
              {t.geo_coverage ?? 0}% of views located · {t.countries_count ?? 0} countries
            </span>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <BarList title="Countries" icon={Globe} data={data.countries || []} />
            <BarList title="States / Regions" icon={MapPin} data={data.regions || []} />
            <BarList title="Cities" icon={Building2} data={data.cities || []} />
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200 p-5" data-testid="demand-insights-panel">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <h3 className="font-bold text-slate-800">Demand Insights</h3>
            <span className="text-xs text-slate-400">What buyers want — use this to decide what to source &amp; market next</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mt-4">
            <StatCard icon={Search} label="Searches" value={t.searches ?? 0} />
            <StatCard icon={PackageX} label="Zero-Result Searches" value={t.zero_result_searches ?? 0} />
            <StatCard icon={ShoppingCart} label="Product Quote Clicks" value={t.quote_clicks ?? 0} />
            <StatCard icon={MessageCircle} label="“More Inventory” Asks" value={t.more_inventory_clicks ?? 0} />
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-5">
            <BarList
              title="Top Searches"
              icon={Search}
              data={data.searches || []}
              subtitle="Most common terms buyers type"
            />
            <BarList
              title="Zero-Result Searches"
              icon={PackageX}
              tone="amber"
              data={data.zero_searches || []}
              subtitle="Demand you can't fulfill yet — source these next"
            />
            <BarList
              title="Most-Quoted Products"
              icon={ShoppingCart}
              data={data.quoted_products || []}
              subtitle="Items generating the most WhatsApp intent"
            />
            <BarList
              title="Quotes by City"
              icon={Building2}
              data={data.quote_cities || []}
              subtitle="Where your real buyers are clustered"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <BarList title="Top Pages" icon={Eye} data={prettyPages} />
          <BarList title="Sections Viewed" icon={Activity} data={data.sections} />
          <BarList title="Devices" icon={Monitor} data={data.devices} iconMap={deviceIcons} />
          <BarList title="Languages" icon={Globe} data={prettyLangs} />
          <BarList title="Top Referrers" icon={Globe} data={data.referrers} />
        </div>
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("inventory");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  useEffect(() => {
    document.title = "Admin · Tele-Comp Solutions";
  }, []);

  if (!authed) return <LoginForm onSuccess={() => setAuthed(true)} />;

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`admin-tab-${id}`}
      title={label}
      className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-sm font-bold transition-colors ${
        tab === id ? "bg-green-600 text-white" : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      <Icon className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-100 overflow-x-hidden">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <img src="/uploads/tcs_logo.png" alt="Tele-Comp" className="h-7 sm:h-8 w-auto" />
            <span className="text-slate-300 hidden sm:inline">|</span>
            <span className="text-slate-400 font-medium hidden sm:inline">Admin</span>
          </div>
          <nav className="flex items-center gap-1 sm:gap-1.5 bg-slate-50 rounded-full p-1">
            <TabBtn id="inventory" icon={Boxes} label="Inventory" />
            <TabBtn id="marketing" icon={Megaphone} label="Marketing" />
            <TabBtn id="popups" icon={Sparkles} label="Popups" />
            <TabBtn id="homepage" icon={LayoutGrid} label="Homepage" />
            <TabBtn id="analytics" icon={BarChart3} label="Analytics" />
          </nav>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            title="Logout"
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border border-slate-200 hover:border-red-300 hover:text-red-600 text-slate-700 font-semibold text-sm transition-colors shrink-0"
          >
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-3 sm:px-6 py-6 sm:py-8">
        {tab === "analytics" ? (
          <Dashboard onLogout={logout} />
        ) : tab === "marketing" ? (
          <AdminMarketing onUnauthorized={logout} />
        ) : tab === "popups" ? (
          <AdminPopups />
        ) : tab === "homepage" ? (
          <div className="space-y-8">
            <AdminSiteMedia />
            <div className="border-t border-slate-200" />
            <AdminSiteCategories />
          </div>
        ) : (
          <AdminInventoryFile />
        )}
      </main>
    </div>
  );
}
